import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import client from "@/lib/api";
import { SignOut, ChartLineUp, ClipboardText, ArrowsLeftRight, ChartBar, TreeStructure, UploadSimple, User, Factory, GlobeHemisphereEast, CaretDown, CaretRight, List, X } from "@phosphor-icons/react";
import { fmtPeriod } from "@/lib/format";

const ICON_MAP = {
    "nav-overview": ChartLineUp,
    "nav-submissions": ClipboardText,
    "nav-intercompany": ArrowsLeftRight,
    "nav-planning": ChartBar,
    "nav-org": TreeStructure,
    "nav-uploads": UploadSimple,
    "nav-myentity": User,
    "nav-mydiv": ChartLineUp,
};

export default function Layout() {
    const { user, logout } = useAuth();
    const [home, setHome] = useState(null);
    const [meta, setMeta] = useState(null);
    const [openSector, setOpenSector] = useState(true);
    const [openCountry, setOpenCountry] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        client.get("/me/home").then((r) => setHome(r.data));
    }, []);

    useEffect(() => {
        if (!home) return;
        if (loc.pathname === "/" && home.home !== "/") {
            window.history.replaceState(null, "", home.home);
            window.location.reload();
            return;
        }
        if (home.capabilities?.can_read_all) {
            client.get("/meta/hierarchy").then((r) => setMeta(r.data)).catch(() => {});
        }
    }, [home]); // eslint-disable-line

    // Close drawer on route change (mobile UX)
    useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

    if (!home) return <div className="min-h-screen flex items-center justify-center font-sans text-sm text-fp-secondary">Loading…</div>;
    const latestPeriod = "2026-01";

    const flatNav = home.nav || [];
    const overviewItem = flatNav.find((n) => n.path === "/");
    const managementItems = flatNav.filter((n) => !["/", `/entity/${home.user.scope?.id}/portal`, `/sector/${home.user.scope?.id}`].includes(n.path));
    const scopedItem = flatNav.find((n) => n.path.includes("/entity/") || (n.path.startsWith("/sector/") && !home.capabilities.can_read_all));

    const sidebarContent = (
        <>
            <div className="p-6 border-b border-fp-border flex items-center justify-between">
                <div className="min-w-0">
                    <div className="font-serif text-xl tracking-tight text-fp-ink leading-tight">Finance Performance</div>
                    <div className="font-sans text-[10px] uppercase tracking-[0.3em] text-fp-muted mt-1">Prototype</div>
                </div>
                <button data-testid="drawer-close" className="lg:hidden text-fp-secondary hover:text-fp-ink" onClick={() => setDrawerOpen(false)} aria-label="Close menu">
                    <X size={20} />
                </button>
            </div>

            <div className="px-4 py-3 border-b border-fp-border">
                <div className="font-sans text-[10px] uppercase tracking-[0.2em] text-fp-muted mb-1">Signed in as</div>
                <div className="font-mono text-[10px] text-fp-ink truncate">{home.user.role_label}</div>
            </div>

            <nav className="flex-1 py-4 px-3 overflow-y-auto text-sm">
                {scopedItem && <NavItem item={scopedItem} />}
                {overviewItem && <NavItem item={overviewItem} />}
                {meta && home.capabilities?.can_read_all && (
                    <>
                        <NavGroup icon={Factory} label="By Sector" open={openSector} onToggle={() => setOpenSector(!openSector)} testId="nav-sectors">
                            {meta.sectors?.map((s) => (
                                <SubNav key={s.id} to={`/sector/${s.id}`} label={s.name} testId={`nav-sector-${s.id}`} />
                            ))}
                        </NavGroup>
                        <NavGroup icon={GlobeHemisphereEast} label="By Country" open={openCountry} onToggle={() => setOpenCountry(!openCountry)} testId="nav-countries">
                            {meta.countries?.map((c) => (
                                <SubNav key={c.code} to={`/country/${c.code}`} label={c.name} testId={`nav-country-${c.code}`} />
                            ))}
                        </NavGroup>
                        <div className="my-3 border-t border-fp-border" />
                    </>
                )}
                {managementItems.filter((n) => n.path !== "/").map((item) => <NavItem key={item.path} item={item} />)}
            </nav>

            <div className="p-4 border-t border-fp-border">
                <div className="font-mono text-[10px] text-fp-muted mb-1">Reporting Period</div>
                <div className="font-mono text-sm text-fp-ink">{fmtPeriod(latestPeriod)}</div>
            </div>

            <div className="p-4 border-t border-fp-border flex items-center justify-between">
                <div className="min-w-0">
                    <div className="font-sans text-sm text-fp-ink truncate">{user?.name}</div>
                    <div className="font-mono text-[10px] text-fp-muted uppercase tracking-wider">{home.user.role}</div>
                </div>
                <button data-testid="logout-btn" onClick={logout} className="text-fp-secondary hover:text-fp-ink transition-colors" title="Sign out">
                    <SignOut size={18} />
                </button>
            </div>
        </>
    );

    return (
        <div className="min-h-screen bg-fp-bg lg:flex">
            {/* Mobile top bar */}
            <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-fp-border flex items-center justify-between px-4 py-3">
                <button data-testid="drawer-open" onClick={() => setDrawerOpen(true)} className="text-fp-ink" aria-label="Open menu">
                    <List size={22} />
                </button>
                <div className="text-center">
                    <div className="font-serif text-base leading-tight text-fp-ink">Finance Performance</div>
                    <div className="font-sans text-[9px] uppercase tracking-[0.25em] text-fp-muted">Prototype</div>
                </div>
                <button data-testid="logout-btn-mobile" onClick={logout} className="text-fp-secondary" title="Sign out" aria-label="Sign out">
                    <SignOut size={18} />
                </button>
            </header>

            {/* Mobile drawer overlay */}
            {drawerOpen && (
                <div className="lg:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setDrawerOpen(false)} data-testid="drawer-overlay" />
            )}

            {/* Sidebar — drawer on mobile, static on desktop */}
            <aside
                className={`${drawerOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky lg:top-0 top-0 left-0 z-50 lg:z-auto h-screen lg:h-screen w-72 lg:w-64 shrink-0 border-r border-fp-border bg-white flex flex-col transition-transform duration-200 ease-out`}
                data-testid="sidebar"
            >
                {sidebarContent}
            </aside>

            <main className="flex-1 min-w-0" key={loc.pathname}>
                <div className="animate-fade-up">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}

const NavItem = ({ item }) => {
    const Icon = ICON_MAP[item.testId] || ChartLineUp;
    return (
        <NavLink
            to={item.path}
            data-testid={item.testId}
            end={item.path === "/"}
            className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 mb-0.5 font-sans text-sm transition-colors ${
                    isActive ? "bg-fp-panel text-fp-ink font-medium" : "text-fp-secondary hover:text-fp-ink hover:bg-fp-panel/50"
                }`
            }
        >
            <Icon size={16} weight="regular" />
            <span>{item.label}</span>
        </NavLink>
    );
};

const NavGroup = ({ icon: Icon, label, open, onToggle, children, testId }) => (
    <div className="mb-1">
        <button data-testid={testId} onClick={onToggle} className="w-full flex items-center gap-3 px-3 py-2 font-sans text-sm text-fp-secondary hover:text-fp-ink transition-colors">
            <Icon size={16} weight="regular" />
            <span className="flex-1 text-left">{label}</span>
            {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
        </button>
        {open && <div className="ml-6 border-l border-fp-border pl-2 mb-2">{children}</div>}
    </div>
);

const SubNav = ({ to, label, testId }) => (
    <NavLink
        to={to}
        data-testid={testId}
        className={({ isActive }) =>
            `block px-3 py-1.5 font-sans text-xs transition-colors ${
                isActive ? "text-fp-ink font-medium" : "text-fp-secondary hover:text-fp-ink"
            }`
        }
    >
        {label}
    </NavLink>
);
